import { useCallback, useEffect, useRef, useState } from 'react'
import { API_ROUTES, type TournamentDetail } from '@brochure/shared'
import { apiFetch } from '@/lib/api'
import { useLive } from './useLive'

/** Torneo con dati live: rilettura via `GET /tournaments/:id`, aggiornata automaticamente dai messaggi WebSocket. */
export function useTournament(id: string | undefined) {
  const [state, setState] = useState<{ id: string | undefined; data?: TournamentDetail; error?: unknown }>({ id: undefined })
  const [version, setVersion] = useState(0)
  const controllerRef = useRef<AbortController | null>(null)

  const reload = useCallback(() => {
    if (!id) return
    controllerRef.current?.abort()
    const controller = new AbortController()
    controllerRef.current = controller
    apiFetch<TournamentDetail>(`${API_ROUTES.tournaments}/${id}`, { signal: controller.signal })
      .then((data) => { if (!controller.signal.aborted) setState({ id, data }) })
      .catch((error: unknown) => { if (!controller.signal.aborted) setState({ id, error }) })
  }, [id])

  useEffect(() => {
    reload()
    return () => controllerRef.current?.abort()
  }, [reload])

  // Il timer non cambia i dati del torneo: solo gli altri eventi (girone, partite, monete) fanno rileggere.
  // `version` invece cresce a ogni messaggio, timer compreso: la tab statistiche lo usa per rileggere `/events`.
  const { isOpen } = useLive(id, (message) => {
    setVersion((v) => v + 1)
    if (message.type !== 'timer') reload()
  })

  const isCurrent = state.id === id
  return {
    data: isCurrent ? state.data : undefined,
    error: isCurrent ? state.error : undefined,
    isLoading: id !== undefined && !isCurrent,
    reload,
    isLive: isOpen,
    version,
  }
}
